'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { brandConfig } from '@/config/brand';
import { marketplaceNav } from '@/config/marketplaceNav';
import { useAuth } from '@/platform/auth/useAuth';
import { useMarketplaceNavVisible } from '@/platform/navTour/useMarketplaceNavVisible';
import { ThemeSettingsModal } from '@/platform/theme/ThemeSettings';
import { ProductNavLink } from '@/platform/lib/ProductNavLink';
import { isOpsItsmBuild } from '@/platform/servicedesk/isOpsItsmBuild';
import { ChromeSearch } from './ChromeSearch';

function isActive(pathname: string, href: string): boolean {
  const base = pathname.replace(/\/$/, '') || '/';
  const target = href.replace(/\/$/, '') || '/';
  if (target === '/') return base === '/';
  return base === target || base.startsWith(`${target}/`);
}

const items: { href: string; label: string; icon: string; tourId: string }[] = [
  { href: '/', label: 'Feed', icon: 'uil-comment-alt', tourId: 'home' },
  { href: brandConfig.forum.path, label: brandConfig.forum.label, icon: 'uil-comments', tourId: 'forums' },
  { href: brandConfig.groups.path, label: brandConfig.groups.label, icon: 'uil-users-alt', tourId: 'groups' },
  { href: brandConfig.calendar.path, label: brandConfig.calendar.label, icon: 'uil-calendar', tourId: 'calendar' },
  { href: brandConfig.blog.path, label: brandConfig.blog.label, icon: 'uil-newspaper', tourId: 'blog' },
  { href: brandConfig.feedback.path, label: 'Feedback', icon: 'uil-comment-alt-dots', tourId: 'feedback' },
];

/** Left rail for Communio Social tenants — forum/groups/calendar/blog (+ marketplace), never haul claims. */
export function CommunioSidebar() {
  if (isOpsItsmBuild()) {
    return null;
  }
  const pathname = usePathname() || '/';
  const { user, profile } = useAuth();
  const { ready: marketplaceReady, visible: marketplaceVisible } = useMarketplaceNavVisible();
  const displayName = profile?.displayName || user?.email || 'Member';
  const [themeOpen, setThemeOpen] = useState(false);

  return (
    <aside className="paydirt-sidebar" data-testid="communio-sidebar">
      {user ? (
        <ProductNavLink href={`/users/${user.uid}/`} className="paydirt-sidebar__profile">
          <div className="profile-photo">{displayName.slice(0, 1).toUpperCase()}</div>
          <div>
            <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{displayName}</h4>
            <p className="text-muted" style={{ margin: 0, fontSize: '0.8rem' }}>
              Community member
            </p>
          </div>
        </ProductNavLink>
      ) : null}
      <ChromeSearch />
      <nav className="paydirt-sidebar__menu" aria-label="Sidebar">
        {items.map((item) => (
          <ProductNavLink
            key={item.href}
            href={item.href}
            data-nav-tour-id={item.tourId}
            className={`paydirt-sidebar__item${isActive(pathname, item.href) ? ' paydirt-sidebar__item--active' : ''}`}
          >
            <i className={`uil ${item.icon}`} aria-hidden />
            <span>{item.label}</span>
          </ProductNavLink>
        ))}
        {marketplaceNav.enabled && marketplaceReady && marketplaceVisible ? (
          <ProductNavLink
            href={marketplaceNav.rootHref}
            data-nav-tour-id="marketplace"
            className={`paydirt-sidebar__item${isActive(pathname, marketplaceNav.rootHref) ? ' paydirt-sidebar__item--active' : ''}`}
          >
            <i className={`uil ${marketplaceNav.shop.icon ?? 'uil-shop'}`} aria-hidden />
            <span>{marketplaceNav.sectionLabel}</span>
          </ProductNavLink>
        ) : null}
        <button
          type="button"
          className="paydirt-sidebar__item"
          data-nav-tour-id="theme"
          onClick={() => setThemeOpen(true)}
        >
          <i className="uil uil-palette" aria-hidden />
          <span>Theme</span>
        </button>
      </nav>
      <ThemeSettingsModal open={themeOpen} onClose={() => setThemeOpen(false)} />
    </aside>
  );
}
